import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PortalAdminLayout from "./PortalAdminLayout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  marginBottom: "6px",
};
const inputStyle = {
  width: "100%",
  marginBottom: "10px",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  padding: "9px",
};
const cardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  padding: "16px",
  background: "#ffffff",
};
const primaryButton = {
  background: "#0f766e",
  color: "white",
  border: "none",
  borderRadius: "8px",
  padding: "10px 14px",
  fontWeight: 600,
  cursor: "pointer",
};

/**
 * Signup promos and R9 referral agents, as a TABS page.
 *
 * The tab list is data, not markup: adding a fourth tab is one entry in TABS plus its panel, which keeps
 * the page from turning into the single long scroll it started as.
 */
const TABS = [
  { key: "promos", label: "Promos" },
  { key: "pending", label: "Pending approvals" },
  { key: "agents", label: "Agents" },
];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}` };
}

function formatDate(value) {
  if (!value) return "Open-ended";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "Open-ended" : d.toLocaleDateString();
}

function ReferralAgentsManagement() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("promos");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [disabled, setDisabled] = useState(false);

  const [promos, setPromos] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pending, setPending] = useState([]);

  const [promoForm, setPromoForm] = useState({
    code: "",
    description: "",
    percentOff: "",
    durationMonths: "",
    maxRedemptions: "",
    validUntil: "",
    agentUserId: "",
    agentCampaign: false,
    core: true,
    plus: true,
  });

  const [inviteForm, setInviteForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [approveForm, setApproveForm] = useState({
    commissionPercent: "",
    commissionCapMonths: "",
  });

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = JSON.parse(
        decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
        ),
      );
      const isR6 =
        decoded.role === "R6" ||
        decoded.role === "portal_admin" ||
        (Array.isArray(decoded.roles) &&
          (decoded.roles.includes("R6") || decoded.roles.includes("portal_admin")));
      if (!isR6) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (_error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const loadEverything = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [promoRes, agentRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/platform-plans/admin/promos`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_BASE_URL}/api/platform-plans/admin/agents`, {
          headers: authHeaders(),
        }),
      ]);
      setDisabled(false);
      setPromos(promoRes.data?.promos || []);
      setAgents(agentRes.data?.agents || []);
      setPending(agentRes.data?.pending || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setDisabled(true);
      } else {
        setError(err.response?.data?.message || "Failed to load promos and agents");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checkPortalAdmin()) {
      loadEverything();
    }
  }, [checkPortalAdmin, loadEverything]);

  const activeAgents = useMemo(
    () =>
      agents.filter((a) => a.status === "active" && a.agentType === "agent"),
    [agents],
  );

  const onCreatePromo = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    const eligiblePlans = [
      promoForm.core ? "Core" : null,
      promoForm.plus ? "Plus" : null,
    ].filter(Boolean);

    if (!promoForm.code.trim() || !promoForm.description.trim()) {
      setError("Code and description are required.");
      return;
    }
    if (!(Number(promoForm.percentOff) > 0)) {
      setError("Percent off must be greater than zero.");
      return;
    }
    if (eligiblePlans.length === 0) {
      setError("Select at least one plan this code applies to.");
      return;
    }

    setSaving(true);
    try {
      // Blank duration means open-ended: the discount never reverts. Never default this in the client.
      const payload = {
        code: promoForm.code.trim().toUpperCase(),
        description: promoForm.description.trim(),
        percentOff: Number(promoForm.percentOff),
        durationMonths: promoForm.durationMonths === "" ? null : Number(promoForm.durationMonths),
        maxRedemptions: promoForm.maxRedemptions === "" ? null : Number(promoForm.maxRedemptions),
        validUntil: promoForm.validUntil ? promoForm.validUntil : null,
        agentUserId: promoForm.agentCampaign ? null : promoForm.agentUserId || null,
        agentCampaign: promoForm.agentCampaign === true,
        eligiblePlans,
      };

      await axios.post(`${API_BASE_URL}/api/platform-plans/admin/promos`, payload, {
        headers: authHeaders(),
      });
      setMessage(`Promo ${payload.code} created.`);
      setPromoForm({
        code: "",
        description: "",
        percentOff: "",
        durationMonths: "",
        maxRedemptions: "",
        validUntil: "",
        agentUserId: "",
        agentCampaign: false,
        core: true,
        plus: true,
      });
      await loadEverything();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create the promo");
    } finally {
      setSaving(false);
    }
  };

  const onInviteAgent = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!inviteForm.email.trim()) {
      setError("An email address is required — that is where the setup link goes.");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/platform-plans/admin/agents/invite`,
        {
          firstName: inviteForm.firstName.trim(),
          lastName: inviteForm.lastName.trim(),
          email: inviteForm.email.trim().toLowerCase(),
        },
        { headers: authHeaders() },
      );
      setMessage(`Invitation sent to ${inviteForm.email.trim()}.`);
      setInviteForm({ firstName: "", lastName: "", email: "" });
      await loadEverything();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to invite that agent");
    } finally {
      setSaving(false);
    }
  };

  const onApproveAgent = async (userId) => {
    setError("");
    setMessage("");
    if (!userId) {
      setError("Choose a pending agent to approve.");
      return;
    }

    setApproving(userId);
    try {
      // Blank overrides fall back to the platform defaults held in PlatformSettings.
      const response = await axios.post(
        `${API_BASE_URL}/api/platform-plans/admin/agents`,
        {
          userId,
          commissionPercent:
            approveForm.commissionPercent === "" ? null : Number(approveForm.commissionPercent),
          commissionCapMonths:
            approveForm.commissionCapMonths === "" ? null : Number(approveForm.commissionCapMonths),
        },
        { headers: authHeaders() },
      );

      setMessage(
        `Approved. Code ${response.data?.agent?.code} — share ${
          response.data?.agent?.link || "the referral link"
        }`,
      );
      await loadEverything();
      setTab("agents");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve the agent");
    } finally {
      setApproving("");
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setMessage("Link copied.");
    } catch (_err) {
      setMessage(link);
    }
  };

  return (
    <PortalAdminLayout>
      <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={{ marginTop: 0, marginBottom: "8px" }}>
          Signup Promos &amp; Referral Agents
        </h1>
        <p style={{ marginTop: 0, color: "#64748b", marginBottom: "16px" }}>
          Discount codes businesses enter at signup, and the R9 agents who earn commission on the
          businesses they bring. Rates, durations and caps are configuration — leave a field blank to
          use the platform defaults.
        </p>

        {error && (
          <div style={{ marginBottom: "16px", padding: "12px", borderRadius: "8px", background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b" }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ marginBottom: "16px", padding: "12px", borderRadius: "8px", background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534" }}>
            {message}
          </div>
        )}

        <div
          role="tablist"
          style={{
            display: "flex",
            gap: "4px",
            borderBottom: "1px solid #e2e8f0",
            marginBottom: "18px",
            overflowX: "auto",
          }}
        >
          {TABS.map((entry) => {
            const active = tab === entry.key;
            const count =
              entry.key === "pending"
                ? pending.length
                : entry.key === "agents"
                  ? agents.length
                  : promos.length;
            return (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(entry.key)}
                style={{
                  border: "none",
                  borderBottom: active ? "2px solid #0f766e" : "2px solid transparent",
                  background: "transparent",
                  padding: "10px 14px",
                  fontSize: "14px",
                  fontWeight: active ? 700 : 500,
                  color: active ? "#0f766e" : "#475569",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {entry.label}
                <span style={{ marginLeft: "6px", fontSize: "12px", color: "#94a3b8" }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {disabled ? (
          <div style={{ padding: "16px", borderRadius: "10px", background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e" }}>
            Plans, promos and referral agents are switched off. Enable
            <code style={{ margin: "0 4px" }}>PLATFORM_PLANS_PROMOS_AGENTS_ENABLED</code>
            to manage them here.
          </div>
        ) : loading ? (
          <div style={{ color: "#64748b" }}>Loading…</div>
        ) : (
          <>
            {tab === "promos" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                <form onSubmit={onCreatePromo} style={{ ...cardStyle, background: "#f8fafc" }}>
                  <h2 style={{ marginTop: 0, fontSize: "18px" }}>Create signup promo</h2>

                  <label style={labelStyle}>Code</label>
                  <input
                    value={promoForm.code}
                    onChange={(e) =>
                      setPromoForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                    }
                    placeholder="LAUNCH25"
                    style={inputStyle}
                  />

                  <label style={labelStyle}>Description</label>
                  <input
                    value={promoForm.description}
                    onChange={(e) =>
                      setPromoForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="25% off the first three months"
                    style={inputStyle}
                  />

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div>
                      <label style={labelStyle}>Percent off</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        step="0.01"
                        value={promoForm.percentOff}
                        onChange={(e) =>
                          setPromoForm((prev) => ({ ...prev, percentOff: e.target.value }))
                        }
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Duration (months)</label>
                      <input
                        type="number"
                        min="1"
                        value={promoForm.durationMonths}
                        onChange={(e) =>
                          setPromoForm((prev) => ({ ...prev, durationMonths: e.target.value }))
                        }
                        placeholder="Blank = open-ended"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Max redemptions</label>
                      <input
                        type="number"
                        min="1"
                        value={promoForm.maxRedemptions}
                        onChange={(e) =>
                          setPromoForm((prev) => ({ ...prev, maxRedemptions: e.target.value }))
                        }
                        placeholder="Blank = unlimited"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Valid until</label>
                      <input
                        type="date"
                        value={promoForm.validUntil}
                        onChange={(e) =>
                          setPromoForm((prev) => ({ ...prev, validUntil: e.target.value }))
                        }
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <label style={labelStyle}>Applies to plans</label>
                  <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
                    <label style={{ fontSize: "14px" }}>
                      <input
                        type="checkbox"
                        checked={promoForm.core}
                        onChange={(e) =>
                          setPromoForm((prev) => ({ ...prev, core: e.target.checked }))
                        }
                      />{" "}
                      Core
                    </label>
                    <label style={{ fontSize: "14px" }}>
                      <input
                        type="checkbox"
                        checked={promoForm.plus}
                        onChange={(e) =>
                          setPromoForm((prev) => ({ ...prev, plus: e.target.checked }))
                        }
                      />{" "}
                      Plus
                    </label>
                  </div>

                  <label style={labelStyle}>Owned by an agent (optional)</label>
                  <select
                    value={promoForm.agentUserId}
                    onChange={(e) =>
                      setPromoForm((prev) => ({ ...prev, agentUserId: e.target.value }))
                    }
                    style={inputStyle}
                    disabled={promoForm.agentCampaign}
                  >
                    <option value="">No agent — platform promo</option>
                    {activeAgents.map((agent) => (
                      <option
                        key={agent.userId?._id || agent.userId}
                        value={agent.userId?._id || agent.userId}
                      >
                        {agent.displayName || agent.code} ({agent.code})
                      </option>
                    ))}
                  </select>
                  <p style={{ fontSize: "12px", color: "#64748b", marginTop: 0 }}>
                    One agent only, for an exception. Leave blank for a platform promo — nobody earns
                    on it.
                  </p>

                  {/*
                    The agent campaign is why 20 agents do not need 20 codes: every agent's own code
                    inherits this one discount. An agent with their own promo still wins over it.
                  */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      fontSize: "13px",
                      marginBottom: "10px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={promoForm.agentCampaign}
                      onChange={(e) =>
                        setPromoForm((prev) => ({
                          ...prev,
                          agentCampaign: e.target.checked,
                          agentUserId: e.target.checked ? "" : prev.agentUserId,
                        }))
                      }
                      style={{ marginTop: "3px" }}
                    />
                    <span>
                      Use this discount for <strong>every agent</strong> (agent campaign). Saving it
                      retires the previous campaign.
                    </span>
                  </label>

                  <button type="submit" disabled={saving} style={primaryButton}>
                    {saving ? "Saving…" : "Create promo"}
                  </button>
                </form>

                <div style={cardStyle}>
                  <h2 style={{ marginTop: 0, fontSize: "18px" }}>Signup promos</h2>
                  {promos.length === 0 ? (
                    <div style={{ color: "#64748b" }}>No signup promos yet.</div>
                  ) : (
                    <div style={{ display: "grid", gap: "10px", maxHeight: "560px", overflow: "auto" }}>
                      {promos.map((promo) => (
                        <div key={promo._id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                            <strong>{promo.code}</strong>
                            <span style={{ fontSize: "12px", color: promo.isActive ? "#166534" : "#991b1b" }}>
                              {promo.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div style={{ fontSize: "13px", color: "#475569" }}>{promo.description}</div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                            {(promo.eligiblePlans || []).join(" + ")} · {promo.percentOff}% off ·{" "}
                            {promo.durationMonths
                              ? `${promo.durationMonths} months`
                              : "open-ended"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>
                            Used {promo.redemptionCount || 0}
                            {promo.maxRedemptions ? ` of ${promo.maxRedemptions}` : ""} · Valid until{" "}
                            {formatDate(promo.validUntil)}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>
                            {promo.agentCampaign
                              ? "Agent campaign — every agent's code uses this"
                              : promo.agentCode
                                ? `Agent ${promo.agentCode}`
                                : "Platform promo — no agent credited"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "pending" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                <form onSubmit={onInviteAgent} style={{ ...cardStyle, background: "#f8fafc" }}>
                  <h2 style={{ marginTop: 0, fontSize: "18px" }}>Invite a referral agent</h2>
                  <p style={{ color: "#64748b", fontSize: "13px" }}>
                    An agent has no account until you create one here. They receive an email link to
                    set their password; approving them afterwards issues their code and share link.
                  </p>

                  <label style={labelStyle}>First name</label>
                  <input
                    value={inviteForm.firstName}
                    onChange={(e) =>
                      setInviteForm((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                    style={inputStyle}
                  />

                  <label style={labelStyle}>Last name</label>
                  <input
                    value={inviteForm.lastName}
                    onChange={(e) =>
                      setInviteForm((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                    style={inputStyle}
                  />

                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="agent@example.com"
                    style={inputStyle}
                  />

                  <button type="submit" disabled={saving} style={primaryButton}>
                    {saving ? "Sending…" : "Send invitation"}
                  </button>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "16px" }}>
                    <div>
                      <label style={labelStyle}>Commission % (optional)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={approveForm.commissionPercent}
                        onChange={(e) =>
                          setApproveForm((prev) => ({ ...prev, commissionPercent: e.target.value }))
                        }
                        placeholder="Default"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Cap months (optional)</label>
                      <input
                        type="number"
                        min="1"
                        value={approveForm.commissionCapMonths}
                        onChange={(e) =>
                          setApproveForm((prev) => ({
                            ...prev,
                            commissionCapMonths: e.target.value,
                          }))
                        }
                        placeholder="Default"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: "12px", color: "#64748b", marginTop: 0 }}>
                    Used when you approve someone below. Blank = the platform default.
                  </p>
                </form>

                <div style={cardStyle}>
                  <h2 style={{ marginTop: 0, fontSize: "18px" }}>Awaiting approval</h2>
                  {pending.length === 0 ? (
                    <div style={{ color: "#64748b" }}>Nobody is waiting for approval.</div>
                  ) : (
                    <div style={{ display: "grid", gap: "10px" }}>
                      {pending.map((person) => (
                        <div key={person._id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                            <strong>{person.name}</strong>
                            <span style={{ fontSize: "12px", color: "#64748b" }}>
                              {person.hasPassword ? "Set up" : "Invited"}
                            </span>
                          </div>
                          <div style={{ fontSize: "13px", color: "#475569" }}>{person.email}</div>
                          <button
                            type="button"
                            onClick={() => onApproveAgent(person._id)}
                            disabled={approving === person._id}
                            style={{ ...primaryButton, marginTop: "8px", padding: "7px 12px" }}
                          >
                            {approving === person._id ? "Approving…" : "Approve & issue code"}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "agents" && (
              <div style={cardStyle}>
                <h2 style={{ marginTop: 0, fontSize: "18px" }}>Referral agents</h2>
                {agents.length === 0 ? (
                  <div style={{ color: "#64748b" }}>No referral agents yet.</div>
                ) : (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {agents.map((agent) => {
                      const user = agent.userId || {};
                      const name =
                        agent.displayName ||
                        user.name ||
                        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                        "Unknown";
                      return (
                        <div key={agent._id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                            <strong>{name}</strong>
                            <span style={{ fontSize: "12px", color: "#64748b" }}>
                              {agent.agentType === "partner" ? "Partner" : "Agent"} · {agent.status}
                            </span>
                          </div>
                          <div style={{ fontSize: "13px", color: "#475569" }}>{user.email || "—"}</div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                            {agent.code} · {agent.commissionPercent}% · cap{" "}
                            {agent.commissionCapMonths
                              ? `${agent.commissionCapMonths} months`
                              : "open-ended"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#334155", marginTop: "4px" }}>
                            <strong>{agent.referrals || 0}</strong> referred ·{" "}
                            <strong>
                              ${(((agent.earnedToDateCents || 0) / 100) || 0).toFixed(2)}
                            </strong>{" "}
                            earned · ${(((agent.thisPeriodCents || 0) / 100) || 0).toFixed(2)} this
                            period
                          </div>
                          {(agent.businessesReferred || []).length > 0 && (
                            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                              {agent.businessesReferred.join(", ")}
                            </div>
                          )}
                          {agent.link && (
                            <div style={{ marginTop: "8px", display: "flex", gap: "8px", alignItems: "center" }}>
                              <code style={{ fontSize: "11px", color: "#334155", overflowWrap: "anywhere" }}>
                                {agent.link}
                              </code>
                              <button
                                type="button"
                                onClick={() => copyLink(agent.link)}
                                style={{ border: "1px solid #cbd5e1", background: "#f8fafc", borderRadius: "6px", padding: "4px 8px", fontSize: "11px", cursor: "pointer" }}
                              >
                                Copy
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PortalAdminLayout>
  );
}

export default ReferralAgentsManagement;
